/**
 * Server-side paging for the Packages and Orders lists (MYP-126).
 *
 * The two lists used to download everything and cut pages in the browser. Now each page is asked
 * of the vendor (`package_list` / `order_list` take `page` and `limit`), so only what is on screen
 * crosses the network. What the vendor can and cannot do was checked live on 8 October 2026 (test
 * account MP66184, read-only):
 *
 * - Both endpoints page correctly and return rows newest first (by id, descending).
 * - `package_list` takes several statuses at once (`status=NEW,ARRIVE`); `order_list` does not —
 *   two `qryStatus` codes answer 0 rows — so an order stage spanning several codes is fetched one
 *   code at a time and merged here (`mergeNewestFirst`).
 * - Searching is exact only: a full tracking number (`inorderno`) or a full order number
 *   (`orderno`, which answers with that one order's detail record rather than a list).
 * - There is no warehouse filter and no sort parameter.
 */

/** The page sizes the customer can pick from. */
export const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;

export type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

export const DEFAULT_PAGE_SIZE: PageSize = 20;

/** `?size=` read back out of a URL. Anything not on offer is the default. */
export function parsePageSize(params: URLSearchParams): PageSize {
  const parsed = Number(params.get('size'));
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(parsed)
    ? (parsed as PageSize)
    : DEFAULT_PAGE_SIZE;
}

/** `?page=` read back out of a URL. Anything unparseable is page 1. */
export function parsePageNumber(params: URLSearchParams): number {
  const parsed = Number(params.get('page'));
  return Number.isFinite(parsed) && parsed >= 1 ? Math.trunc(parsed) : 1;
}

/** How many pages `total` rows need. Always at least 1, so "page 1 of 0" cannot happen. */
export function pageCountFor(total: number, size: number): number {
  if (!Number.isFinite(total) || total <= 0 || size <= 0) return 1;
  return Math.ceil(total / size);
}

/** Clamps a page into the range that exists — a bookmarked `?page=9` can outlive its rows. */
export function clampPageNumber(page: number, total: number, size: number): number {
  if (!Number.isFinite(page)) return 1;
  return Math.min(Math.max(Math.trunc(page), 1), pageCountFor(total, size));
}

/** A slot in the page strip: a page number, or a gap (`…`) standing for the pages skipped. */
export type PageSlot = number | 'gap';

/**
 * The page numbers to draw: always the first and last, the current page with `siblings` either
 * side, and a gap wherever pages are skipped — `1 … 4 5 6 … 16`.
 *
 * A gap is only drawn when it hides at least two pages. Hiding one page behind `…` takes the same
 * space as showing it, and makes the reader guess a number they could simply have been shown.
 */
export function pageWindow(page: number, pageCount: number, siblings = 1): readonly PageSlot[] {
  const last = Math.max(1, Math.trunc(pageCount));
  const current = Math.min(Math.max(Math.trunc(page), 1), last);

  const wanted = new Set<number>([1, last]);
  for (let offset = -siblings; offset <= siblings; offset += 1) {
    const candidate = current + offset;
    if (candidate >= 1 && candidate <= last) wanted.add(candidate);
  }

  const sorted = [...wanted].sort((a, b) => a - b);
  const slots: PageSlot[] = [];

  sorted.forEach((value, index) => {
    const previous = sorted[index - 1];
    if (previous !== undefined) {
      const skipped = value - previous - 1;
      if (skipped === 1) slots.push(previous + 1);
      else if (skipped > 1) slots.push('gap');
    }
    slots.push(value);
  });

  return slots;
}

/** The "21–40 of 309" numbers for a page. `end` is what is actually on it, not the page size. */
export function pageRange(
  page: number,
  size: number,
  rowsOnPage: number,
): { readonly start: number; readonly end: number } {
  const start = (page - 1) * size + 1;
  return { start, end: start + Math.max(0, rowsOnPage) - 1 };
}

/** A vendor row's numeric id, or undefined — used to merge lists newest first. */
function rowId(row: unknown): number | undefined {
  if (typeof row !== 'object' || row === null) return undefined;
  const value = (row as Record<string, unknown>)['id'];
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Several newest-first vendor lists merged into one, newest first, with duplicates dropped.
 *
 * For an order stage that spans several status codes (`order_list` takes one code at a time):
 * each code's first `page × size` rows are fetched, merged here, and the page is cut from the
 * merged list. Correct because the vendor orders every list the same way, by id descending —
 * checked live. Rows without a readable id sort last rather than being lost.
 */
export function mergeNewestFirst(lists: readonly (readonly unknown[])[]): readonly unknown[] {
  const seen = new Set<number>();
  const merged: { row: unknown; id: number }[] = [];

  for (const list of lists) {
    for (const row of list) {
      const id = rowId(row);
      if (id !== undefined) {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      merged.push({ row, id: id ?? Number.NEGATIVE_INFINITY });
    }
  }

  return merged.sort((a, b) => b.id - a.id).map((entry) => entry.row);
}
